import React, { useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { GDAIChatSource, NovaChatMessage } from '@/types';
import {
  novaThemes,
  novaDisclaimer,
  novaRichTextTableClassName,
  novaTableVars,
  novaWidgetRichTextClassName,
  NovaThemeName,
} from './novaTheme';
import {
  extractInlineSources,
  mergeSources,
  mergeSuggestedQuestions,
  dedupeSourcesByLink,
  buildNovaExportHtml,
  linkCitationMarkers,
  findImageSource,
  imageCaption,
} from './novaAnswerParser';
import { useNovaResponseActions } from '@/hooks/useNovaResponseActions';
import Tooltip from '../general/Tooltip';
import NovaToast from './NovaToast';
import NovaInfoDialog from './NovaInfoDialog';
import NovaDownloadDialog from './NovaDownloadDialog';
import { NovaCopyIcon, NovaDownloadIcon, NovaGridIcon, NovaInfoIcon } from './novaIcons';
import { AppInsightsContext, TrackEventKey, TrackEventName, TrackEventType, UserContext } from '@/components';

// The widget's inline gallery shows one row of images (3 across at sm+, matching the
// grid below), with the rest reachable via "View all images".
const GALLERY_PREVIEW_COUNT = 3;

interface NovaMessageProps {
  message: NovaChatMessage;
  theme: NovaThemeName;
  isFirst?: boolean;
  onFollowUpClick?: (question: string) => void;
  followUpQuestions?: string[];
  onSave?: () => void;
  saveDisabled?: boolean;
  saveLabel?: string;
  /** The user question this assistant message answered — shown in the Info dialog. */
  searchTerm?: string;
  published12mFilter?: boolean;
}

const NovaMessage: React.FC<NovaMessageProps> = ({
  message,
  theme,
  isFirst,
  onFollowUpClick,
  followUpQuestions,
  onSave,
  saveDisabled,
  saveLabel,
  searchTerm,
  published12mFilter,
}) => {
  const { role, content, sources, images, timestamp } = message;
  const surfaces = novaThemes[theme];
  const appInsights = useContext(AppInsightsContext);
  const user = useContext(UserContext);
  const [galleryExpanded, setGalleryExpanded] = useState(false);
  const visibleImages = useMemo(
    () => (galleryExpanded ? images : images?.slice(0, GALLERY_PREVIEW_COUNT)),
    [images, galleryExpanded],
  );

  // Strips the model's own "Sources"/"References" heading out of the flow — its
  // links become a fallback source list, used only when the backend doesn't send
  // structured `sources`. Its inline [1][2] markers are left in `rawHtml` as-is;
  // they're resolved into real links below, once `effectiveSources` is known.
  const { html: rawHtml, parsedSources, relatedQuestions } = useMemo(
    () => (role === 'assistant' ? extractInlineSources(content) : { html: content, parsedSources: [], relatedQuestions: [] }),
    [role, content],
  );
  const effectiveSources: GDAIChatSource[] = useMemo(() => mergeSources(sources, parsedSources), [sources, parsedSources]);
  // AVA doesn't actually send a structured `suggestedQuestions` field today (see
  // novaAnswerParser) — `followUpQuestions` (from the stream, always empty in
  // practice) wins if that ever changes, otherwise fall back to the model's own
  // "Related Questions" list parsed out of this message's text.
  const effectiveFollowUpQuestions = useMemo(
    () => mergeSuggestedQuestions(followUpQuestions, relatedQuestions),
    [followUpQuestions, relatedQuestions],
  );
  // Each [n] marker becomes a link to the source (or, matching an image's own
  // citation_number, the image) it actually names (see linkCitationMarkers) rather
  // than just trusting n matches a chip's position.
  const html = useMemo(
    () => (role === 'assistant' ? linkCitationMarkers(rawHtml, effectiveSources, images) : rawHtml),
    [role, rawHtml, effectiveSources, images],
  );
  // A copied/exported answer strips the markers instead — a citation number with no
  // chip next to it to point at would just be noise once it's out of this UI.
  // One chip per article — the citations event can list one article several times
  // (text plus its figures); `effectiveSources` keeps every number so inline badges resolve.
  const referenceSources = useMemo(() => dedupeSourcesByLink(effectiveSources), [effectiveSources]);
  // Plus a References list of real links in place of the model's own sources text.
  const htmlForCopy = useMemo(
    () => (role === 'assistant' ? buildNovaExportHtml(content, effectiveSources, images) : rawHtml),
    [role, content, effectiveSources, images, rawHtml],
  );

  const {
    copied,
    infoOpen,
    setInfoOpen,
    downloadOpen,
    setDownloadOpen,
    exportToast,
    exportError,
    exporting,
    copyToClipboard,
    handleExport,
  } = useNovaResponseActions(htmlForCopy);

  const trackReferenceClick = (source: GDAIChatSource) => {
    appInsights.trackCustomEvent(TrackEventName.NOVA, TrackEventKey.REFERENCE, TrackEventType.CLICK, user, {
      ref_link: source.meta_Link,
      ref_title: source.title,
    });
  };

  return (
    <div
      className={classNames('w-fit max-w-[85%] rounded-2xl p-3', role === 'user' ? 'ml-auto' : 'mr-auto')}
      style={{
        background: role === 'user' ? surfaces.surfaces.userBubble : surfaces.surfaces.novaBubble,
        color: role === 'user' ? surfaces.ink.userBubbleText : surfaces.ink.body,
      }}
    >
      {role === 'assistant' && visibleImages && visibleImages.length > 0 && (
        <>
          <div className="mb-3 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            {visibleImages.map((image) => {
              const imageSource = findImageSource(image, effectiveSources);
              const caption = imageCaption(image);
              const thumbnail = (
                <>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={image.url} alt={caption} className="h-24 w-full object-cover" />
                  {caption && (
                    <div className="truncate px-1.5 py-1 text-[11px]" style={{ color: surfaces.ink.secondary }} title={caption}>
                      {caption}
                    </div>
                  )}
                </>
              );
              return (
                <div
                  key={image.chunk_id}
                  className="overflow-hidden rounded-lg"
                  style={{ background: surfaces.surfaces.field }}
                >
                  {imageSource ? (
                    <a
                      href={imageSource.meta_Link}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={imageSource.title ? `Open report: ${imageSource.title}` : 'Open source report'}
                      title={imageSource.title || undefined}
                      className="block cursor-pointer"
                      onClick={() => trackReferenceClick(imageSource)}
                    >
                      {thumbnail}
                    </a>
                  ) : (
                    thumbnail
                  )}
                </div>
              );
            })}
          </div>

          {images && images.length > GALLERY_PREVIEW_COUNT && (
            <div className="mb-3 flex justify-end">
              <button
                type="button"
                onClick={() => setGalleryExpanded((v) => !v)}
                className="flex cursor-pointer items-center gap-1.5 text-sm font-medium"
                style={{ color: surfaces.ink.heading }}
              >
                {galleryExpanded ? 'Show fewer images' : 'View all images'}
                <NovaGridIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </>
      )}

      {role === 'assistant' ? (
        <div
          className={`richText text-[15px] leading-[22px] [&_a]:no-underline [&_a:hover]:text-[color:var(--nova-link-hover)] ${novaWidgetRichTextClassName} ${novaRichTextTableClassName}`}
          style={novaTableVars(surfaces) as React.CSSProperties}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <div>{content}</div>
      )}

      {role === 'assistant' && isFirst && (
        <div className="mt-3 text-xs" style={{ color: surfaces.ink.placeholder }}>
          {novaDisclaimer.long}
        </div>
      )}

      {role === 'assistant' && referenceSources.length > 0 && (
        <div className="mt-3 border-t pt-2.5" style={{ borderColor: surfaces.surfaces.hairline }}>
          <div className="mb-2 text-[11px] tracking-[0.14em] uppercase" style={{ color: surfaces.ink.secondary }}>
            Reference content
          </div>
          <div className="flex flex-wrap gap-1.5">
            {referenceSources.map((source, index) => (
              <a
                key={index}
                href={source.meta_Link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border py-1.5 pr-3 pl-1.5 text-xs no-underline hover:text-[color:var(--nova-link-hover)]"
                style={
                  {
                    background: surfaces.surfaces.field,
                    borderColor: surfaces.surfaces.panelBorder,
                    color: surfaces.ink.body,
                    ...novaTableVars(surfaces),
                  } as React.CSSProperties
                }
                onClick={() => trackReferenceClick(source)}
              >
                <span
                  className="flex h-4 w-4 flex-none items-center justify-center rounded-full text-[10px] font-semibold"
                  style={{ background: surfaces.surfaces.userBubble, color: surfaces.ink.userBubbleText }}
                >
                  {source.id}
                </span>
                {source.title}
              </a>
            ))}
          </div>
        </div>
      )}

      {role === 'assistant' && effectiveFollowUpQuestions.length > 0 && (
        <div className="mt-3">
          <div className="mb-2 text-[11px] tracking-[0.14em] uppercase" style={{ color: surfaces.ink.secondary }}>
            Related Questions
          </div>
          <div className="flex flex-wrap gap-2">
            {effectiveFollowUpQuestions.map((question, index) => (
              <button
                key={index}
                type="button"
                onClick={() => onFollowUpClick?.(question)}
                className="cursor-pointer rounded-full border px-3 py-1 text-sm"
                style={{ borderColor: surfaces.surfaces.panelBorder, color: surfaces.ink.body }}
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {role === 'assistant' && (
        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3" style={{ color: surfaces.ink.chromeIcon }}>
            <Tooltip placement="top" content="Copy">
              <button type="button" aria-label="Copy" onClick={copyToClipboard}>
                <NovaCopyIcon className="h-4 w-4 cursor-pointer" />
              </button>
            </Tooltip>
            <button type="button" aria-label="More info" onClick={() => setInfoOpen(true)}>
              <NovaInfoIcon className="h-4 w-4 cursor-pointer" />
            </button>
            <Tooltip placement="top" content="Download">
              <button type="button" aria-label="Download" onClick={() => setDownloadOpen(true)}>
                <NovaDownloadIcon className="h-4 w-4 cursor-pointer" />
              </button>
            </Tooltip>
          </div>
          {onSave && (
            <Tooltip placement="bottom" content={saveLabel || 'Save to scrapbook'}>
              <button
                type="button"
                onClick={onSave}
                disabled={saveDisabled}
                className="cursor-pointer rounded-full px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
                style={{ background: surfaces.actionAccent, color: '#1B190F' }}
              >
                {saveLabel || 'Save to scrapbook'}
              </button>
            </Tooltip>
          )}
        </div>
      )}

      {(copied || exportToast || exportError) && (
        <div className="mt-2">
          <NovaToast
            theme={theme}
            variant={exportError ? 'error' : 'success'}
            message={exportError ? 'Export failed — please try again' : copied ? 'Copied with formatting' : 'Export complete'}
          />
        </div>
      )}

      {role === 'assistant' && (
        <>
          <NovaInfoDialog
            open={infoOpen}
            theme={theme}
            searchTerm={searchTerm}
            createdAt={timestamp}
            sourceCount={referenceSources.length}
            published12mFilter={published12mFilter}
            onClose={() => setInfoOpen(false)}
          />
          <NovaDownloadDialog
            open={downloadOpen}
            theme={theme}
            defaultName={searchTerm || 'Nova report'}
            onClose={() => setDownloadOpen(false)}
            exporting={exporting}
            onExport={handleExport}
          />
        </>
      )}
    </div>
  );
};

export default NovaMessage;
